import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";

// styles and images
import "../project/Project.css";
import "../users/Users.css";
import Add from "../../assets/add.svg";
import CirlceCheck from "../../assets/check_circle.svg";
import CircleMinus from "../../assets/circle-minus.svg";

// store
import { adminDataStore, messageAndErrorStore, userStore } from "../../store/Store";

// components
import DeleteSelected from "../../components/DeleteSelected";
import DeletePopup from "../../components/DeletePopup";
import ErrorDisplay from "../../components/ErrorDisplay";
import AddEditAdminUser from "./AddEditAdminUser";
import useGetFetch from "../../hooks/useGetFetch";
import Loading from "../../components/Loading";

export default function AdminUserList() {
  // global
  const JwtToken = userStore((data) => data.JwtToken);
  const currentUser = userStore((data) => data.currentUser);
  const adminUsers = adminDataStore((data) => data.adminUsers);
  const setAdminUsers = adminDataStore((data) => data.setAdminUsers);
  const adminCompanyData = adminDataStore((data) => data.adminCompanyData);
  const setAdminCompanyData = adminDataStore(
    (data) => data.setAdminCompanyData
  );
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  const [docID, setDocId] = useState("");
  const [deleteTrigger, setDeleteTrigger] = useState(false);
  const [error, setError] = useState(null);
  const [addTrigger, setAddTrigger] = useState(false);
  const [approvalPending, setApprovalPending] = useState(false);
  const [adminPending, setAdminPending] = useState(false);
  const navigate = useNavigate();

  const { isPending: usersPending, error: usersError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/admin/admin-users`,
    setAdminUsers
  );

  const isAdmin = (u) => {
    const user = adminCompanyData.filter(
      (x) => x.id === u.company_id && x.primary_user_id === u.id
    );
    if (user.length > 0) {
      return "Yes";
    } else {
      return "No";
    }
  };

  const handleAdmin = (u) => {
    setAdminPending(true);
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const reqBody = u;
    reqBody.updated_by_id = currentUser.id;
    const requestOptions = {
      method: "PATCH",
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };
    fetch(
      `${process.env.REACT_APP_BACKEND}/admin/company-admin`,
      requestOptions
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setAdminPending(false);
            navigate("/login");
          } else {
            setAdminPending(false);
            toggleNewMessage("Something Went Wrong");
            setError("Something Went Wrong");
            return Promise.resolve();
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (!data) {
          return;
        } else {
          setAdminCompanyData(data);
          setAdminPending(false);
          toggleNewMessage(null, `${u.first_name} Updated`);
        }
      })
      .catch((err) => {
        setAdminPending(false);
        toggleNewMessage(err);
        console.log(err);
        setError(err);
        return;
      })
      .finally(() => {
        setAdminPending(false);
      });
    return () => controller.abort();
  };

  const handleClick = (u) => {
    setApprovalPending(true);
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const reqBody = u;
    if (u.is_approved) {
      reqBody.is_approved = false;
    } else {
      reqBody.is_approved = true;
    }
    reqBody.updated_by_id = currentUser.id;
    const requestOptions = {
      method: "PATCH",
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };
    
    fetch(
      `${process.env.REACT_APP_BACKEND}/admin/user-approval`,
      requestOptions
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setApprovalPending(false);
            navigate("/login");
          } else {
            setApprovalPending(false);
            toggleNewMessage("Something Went Wrong");
            setError("Something Went Wrong");
            return Promise.resolve();
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (!data) {
          return;
        } else {
          setAdminUsers(data);
          setApprovalPending(false);
          toggleNewMessage(null, `${u.first_name} Updated`);
        }
      })
      .catch((err) => {
        setApprovalPending(false);
        toggleNewMessage(err);
        setError(err);
        return;
      })
      .finally(() => setApprovalPending(false));
    return () => controller.abort();
  };
  
  if (error || usersError) {
    return <ErrorDisplay code={500} message={"Something Went Wrong"} />;
  }
  if (usersPending) {
    return <Loading />;
  }
  return (
    adminUsers && (
      <div>
        <div className="d-flex justify-content-between">
          <h1>Users</h1>
          <img
            src={Add}
            alt="add"
            onClick={() => setAddTrigger(true)}
            className="project-delete-image"
          />
        </div>
        <div className="load-display-list">
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                <th>Company</th>
                <th>Company Admin</th>
                <th>Role</th>
                <th>Display Name</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Approved</th>
                <th>Delete User</th>
              </tr>
            </thead>
            <tbody>
              {adminUsers.map((u) => (
                <tr key={u.id}>
                  <td>{u.company_name}</td>
                  {!adminPending &&
                  u.role_id === 4 &&
                  isAdmin(u) === "No" &&
                  u.is_approved ? (
                    <td>
                      <img
                        src={CircleMinus}
                        alt="circle-minus"
                        onClick={() => handleAdmin(u)}
                        className="edit"
                      />
                    </td>
                  ) : !adminPending &&
                    u.role_id === 4 &&
                    isAdmin(u) === "Yes" &&
                    u.is_approved ? (
                    <td>
                      <img
                        src={CirlceCheck}
                        alt="circle-chek"
                        className="edit"
                      />
                    </td>
                  ) : (
                    <td></td>
                  )}
                  <td>{u.role_name}</td>
                  <td>{u.display_name}</td>
                  <td>{u.first_name}</td>
                  <td>{u.last_name}</td>
                  <td>{u.email}</td>
                  {!approvalPending &&
                  isAdmin(u) === "No" &&
                  u.role_id !== 3 ? (
                    <td>
                      <ToggleButtonGroup
                        value={u.is_approved ? "yes" : "no"}
                        exclusive
                        onChange={() => handleClick(u)}
                        aria-label="PermissionStatus"
                        size="small"
                      >
                        <ToggleButton
                          value="yes"
                          color="success"
                          id={u.id}
                          sx={{ borderRadius: "20px" }}
                        >
                          Yes
                        </ToggleButton>
                        <ToggleButton
                          value="no"
                          color="warning"
                          id={u.id}
                          sx={{ borderRadius: "20px" }}
                        >
                          No
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </td>
                  ) : (
                    <td></td>
                  )}
                  {isAdmin(u) === "No" && u.role_id !== 3 ? (
                    <td className="action">
                      <div className="container">
                        <DeleteSelected
                          setDeleteIsTriggered={setDeleteTrigger}
                          setId={setDocId}
                          id={u.id}
                        />
                      </div>
                    </td>
                  ) : (
                    <td></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {deleteTrigger && (
            <DeletePopup
              setTrigger={setDeleteTrigger}
              id={docID}
              table={"admin_users_tbl"}
              endpoint={"user"}
              nameField={"first_name"}
              projectId={0}
              setDocId={setDocId}
              setState={setAdminUsers}
            />
          )}
          {addTrigger && <AddEditAdminUser setAddTrigger={setAddTrigger} />}
        </div>
      </div>
    )
  );
}
